/**
 * PropertyShareButton.jsx — Bouton Partager (dropdown) pour la fiche Bien
 *
 * Gère :
 *  - Génération / récupération du `share_token` du bien
 *  - Copie du lien public /f/{token} dans le presse-papiers
 *  - Envoi de la fiche par email :
 *      • méthode `resend`       → modal d'envoi (multi-destinataires) via /send-fiche
 *      • méthode `local_mailto` → ouverture du client mail (mailto:)
 *      • fallback automatique sur mailto si l'envoi Resend échoue (502/503)
 *  - Ouverture directe de la fiche publique
 *
 * Endpoints : POST /api/properties/{id}/share, POST /api/properties/{id}/send-fiche
 */

import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import RecipientsInput from './RecipientsInput';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PropertyShareButton = ({ propertyId, propertyTitle = 'Bien' }) => {
  const [open, setOpen] = useState(false);
  const [shareUrl, setShareUrl] = useState(null);
  const [emailSendMethod, setEmailSendMethod] = useState(null);
  const [copied, setCopied] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  // Modal d'envoi email (Resend)
  const [emailModalOpen, setEmailModalOpen] = useState(false);
  const [recipients, setRecipients] = useState([]);
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const menuRef = useRef(null);

  // Fermer le dropdown si clic hors du bouton
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const ensureShareUrl = async () => {
    if (shareUrl) return { url: shareUrl, method: emailSendMethod };
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('token');
      const resp = await axios.post(
        `${API}/properties/${propertyId}/share`,
        {},
        token ? { headers: { Authorization: `Bearer ${token}` } } : {}
      );
      const raw = resp.data.share_url || `/f/${resp.data.share_token}`;
      const publicUrl = /^https?:\/\//i.test(raw) ? raw : `${window.location.origin}${raw}`;
      const method = resp.data.email_send_method || 'resend';
      setShareUrl(publicUrl);
      setEmailSendMethod(method);
      return { url: publicUrl, method };
    } catch (e) {
      const status = e?.response?.status;
      if (status === 401) {
        setError('Session expirée — veuillez vous reconnecter');
      } else {
        setError(e?.response?.data?.detail || 'Impossible de générer le lien');
      }
      return { url: null, method: null };
    } finally {
      setLoading(false);
    }
  };

  const openMailto = (url) => {
    const subject = encodeURIComponent(`Fiche du bien — ${propertyTitle}`);
    const body = encodeURIComponent(
      `Bonjour,\n\nVeuillez trouver la fiche complète du bien ${propertyTitle} à cette adresse :\n${url}\n\nCordialement.`
    );
    window.location.href = `mailto:?subject=${subject}&body=${body}`;
  };

  const handleCopy = async () => {
    const { url } = await ensureShareUrl();
    if (!url) return;
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch (e) {
      window.prompt('Copiez ce lien :', url);
    }
  };

  // "Email avec lien" : décide entre modal Resend ou mailto selon la config agence
  const handleEmail = async () => {
    const { url, method } = await ensureShareUrl();
    if (!url) return;
    if (method === 'local_mailto') {
      openMailto(url);
      setOpen(false);
      return;
    }
    // Resend (défaut) → ouvrir le modal d'envoi
    setOpen(false);
    setEmailModalOpen(true);
  };

  const handleSendFiche = async () => {
    if (recipients.length === 0) {
      toast.error('Veuillez ajouter au moins un destinataire');
      return;
    }
    setSending(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/properties/${propertyId}/send-fiche`,
        {
          recipients: recipients.map((r) => ({ email: r.email, contact_id: r.contact_id || null })),
          message: message.trim() || null,
        },
        token ? { headers: { Authorization: `Bearer ${token}` } } : {}
      );
      toast.success(
        recipients.length > 1
          ? `Fiche envoyée à ${recipients.length} destinataires`
          : `Fiche envoyée à ${recipients[0].email}`
      );
      setEmailModalOpen(false);
      setRecipients([]);
      setMessage('');
    } catch (e) {
      const status = e?.response?.status;
      // Service non configuré ou échec Resend → fallback mailto
      if (status === 502 || status === 503) {
        toast.info("Envoi automatique indisponible — ouverture de votre messagerie");
        if (shareUrl) openMailto(shareUrl);
        setEmailModalOpen(false);
      } else if (status === 401) {
        toast.error('Session expirée — veuillez vous reconnecter');
      } else {
        toast.error(e?.response?.data?.detail || "Échec de l'envoi de la fiche");
      }
    } finally {
      setSending(false);
    }
  };

  const handleOpen = async () => {
    const { url } = await ensureShareUrl();
    if (!url) return;
    window.open(url, '_blank', 'noopener,noreferrer');
    setOpen(false);
  };

  return (
    <div className="relative" ref={menuRef}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid="property-share-btn"
        className="inline-flex items-center gap-1.5 h-9 px-3 border border-slate-300 hover:bg-slate-50 rounded-lg text-sm font-medium text-slate-700 transition"
        title="Partager ce bien"
      >
        {/* lot AV6 — icône bleue #007BFF, libellé gris, contour neutre (même règle partout). */}
        <i className={`bi ${loading ? 'bi-hourglass-split animate-spin' : 'bi-share'} text-[#007BFF]`} />
        <span className="hidden sm:inline">Partager</span>
        <i className="bi bi-chevron-down text-[10px]" />
      </button>
      {open && (
        <div
          className="absolute right-0 top-full mt-1.5 bg-white border border-slate-200 rounded-lg shadow-xl min-w-[260px] z-50 py-1"
          data-testid="property-share-menu"
        >
          {error && (
            <div className="px-3 py-2 bg-rose-50 text-rose-700 text-xs border-b border-rose-100">
              <i className="bi bi-exclamation-circle mr-1" />
              {error}
            </div>
          )}
          <button
            type="button"
            onClick={handleEmail}
            data-testid="property-share-email-btn"
            className="w-full text-left px-3 py-2 hover:bg-blue-50 flex items-center gap-2 text-sm text-slate-700 transition"
          >
            <i className="bi bi-envelope text-blue-500" />
            <span>Envoyer par email</span>
          </button>
          <button
            type="button"
            onClick={handleCopy}
            data-testid="property-share-copy-btn"
            className="w-full text-left px-3 py-2 hover:bg-blue-50 flex items-center gap-2 text-sm text-slate-700 transition"
          >
            <i className={`bi ${copied ? 'bi-check2-circle text-emerald-500' : 'bi-link-45deg text-blue-500'}`} />
            <span>{copied ? 'Lien copié !' : 'Copier le lien public'}</span>
          </button>
          <button
            type="button"
            onClick={handleOpen}
            data-testid="property-share-open-btn"
            className="w-full text-left px-3 py-2 hover:bg-blue-50 flex items-center gap-2 text-sm text-slate-700 transition"
          >
            <i className="bi bi-box-arrow-up-right text-blue-500" />
            <span>Ouvrir la fiche publique</span>
          </button>
          {shareUrl && (
            <div
              className="px-3 py-2 border-t border-slate-100 text-[11px] text-slate-500 break-all font-mono"
              data-testid="property-share-url"
            >
              {shareUrl}
            </div>
          )}
        </div>
      )}

      {/* Modal d'envoi de la fiche par email (Resend) */}
      {emailModalOpen && (
        <div
          className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center z-[70] p-4"
          onClick={() => !sending && setEmailModalOpen(false)}
          data-testid="send-fiche-modal"
        >
          <div
            className="bg-white rounded-xl shadow-2xl max-w-lg w-full flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white px-5 py-4 rounded-t-xl flex items-center justify-between">
              <div>
                <h3 className="text-base font-bold flex items-center gap-2">
                  <i className="bi bi-envelope-paper" /> Envoyer la fiche par email
                </h3>
                <p className="text-blue-100 text-xs mt-0.5 truncate">{propertyTitle}</p>
              </div>
              <button
                onClick={() => setEmailModalOpen(false)}
                disabled={sending}
                data-testid="send-fiche-close"
                className="text-white/90 hover:bg-white/20 rounded-lg p-1.5 disabled:opacity-50"
              >
                <i className="bi bi-x-lg text-lg" />
              </button>
            </div>

            <div className="p-5 space-y-4">
              <div>
                <label className="block text-xs font-bold text-slate-600 mb-1.5">
                  DESTINATAIRE(S)
                </label>
                <RecipientsInput value={recipients} onChange={setRecipients} testId="send-fiche-recipients" />
              </div>
              <div>
                <label className="block text-xs font-bold text-slate-600 mb-1.5">
                  MESSAGE (optionnel)
                </label>
                <textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  rows={4}
                  placeholder="Ajoutez un mot personnalisé…"
                  data-testid="send-fiche-message"
                  className="w-full px-3 py-2.5 border-2 border-slate-200 rounded-lg focus:border-blue-500 outline-none text-sm leading-relaxed resize-none"
                />
              </div>
              <div className="text-[11px] text-slate-500 bg-slate-50 border border-slate-100 rounded-lg px-3 py-2 break-all">
                <i className="bi bi-link-45deg mr-1 text-blue-500" />
                Lien partagé : <span className="font-mono">{shareUrl}</span>
              </div>
            </div>

            <div className="px-5 py-3 border-t border-slate-200 bg-slate-50 flex items-center justify-end gap-2 rounded-b-xl">
              <button
                onClick={() => setEmailModalOpen(false)}
                disabled={sending}
                data-testid="send-fiche-cancel"
                className="px-3 py-2 text-sm font-medium text-slate-700 hover:bg-white border border-slate-200 rounded-lg disabled:opacity-50"
              >
                Annuler
              </button>
              <button
                onClick={handleSendFiche}
                disabled={sending}
                data-testid="send-fiche-submit"
                className="px-4 py-2 bg-[#0062CC] hover:bg-[#0056b3] disabled:bg-slate-300 text-white rounded-lg text-sm font-semibold flex items-center gap-2"
              >
                <i className={`bi ${sending ? 'bi-arrow-repeat animate-spin' : 'bi-send'}`} />
                {sending ? 'Envoi…' : 'Envoyer'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PropertyShareButton;
